/**
 * SketchWall + SketchLightbox — faithful to the original ICP: a dark section with
 * a radial glow, a horizontal-scroll "wall" with faded edges, each sketch a
 * cork-pinned paper mat (#f7f4ee) holding a 3/4 image, title below on the dark
 * ground, and a staggered slide-in as boards enter view. The lightbox shows the
 * matted image with a slide-up caption, prev/next arrows, and ←/→/Esc.
 * Edit mode (team): add (upload), inline title + a type select, delete.
 * (A per-sketch "legend" field existed briefly — removed as awkward to maintain.)
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import EditableText from './EditableText'
import { useEditable, usePresentation } from './PresentationContext'
import { createSketch, patchSketch, deleteSketch, reorderSketches } from '../services/presentations'
import { uploadImage } from '../services/uploads'
import { confirmDialog } from '../components/Dialog.jsx'
import { usePdfPagePicker } from '../components/PdfPagePickerModal.jsx'
import { loadPdfjs } from '../lib/pdfjs.js'
import { uploadConceptDrawing, getConceptDrawingsForSketches, getConceptDrawingsExistForClient, deleteConceptDrawing } from '../services/conceptDrawings.js'

const SKETCH_TYPES = ['', 'Site Plan', 'Floor Plan', 'Exterior Elevation', 'Interior Elevation',
  'Building Section', 'Wall Section', 'Roof Plan', 'Perspective', 'Diagram', 'Detail', 'Other']

const IS_PDF = f => /\.pdf$/i.test(f?.name || '') || f?.type === 'application/pdf'

/** Render the PDF's first page to an offscreen canvas — used both as the sketch board's
    display thumbnail (uploaded to the same Cloudinary/Supabase path an image sketch uses)
    and as the seed for ConceptDrawingModal's viewer. Exported for reuse by SketchManager.jsx
    (the journey Sketch ribbon's PDF upload path — same treatment, different entry point). */
export async function renderPdfThumbnail(file) {
  const pdfjs = await loadPdfjs()
  if (!pdfjs) throw new Error('PDF preview could not load')
  const buf = await file.arrayBuffer()
  const doc = await pdfjs.getDocument({ data: buf }).promise
  const page = await doc.getPage(1)
  const vp1 = page.getViewport({ scale: 1 })
  const scale = 640 / vp1.width
  const vp = page.getViewport({ scale })
  const canvas = document.createElement('canvas')
  canvas.width = Math.ceil(vp.width); canvas.height = Math.ceil(vp.height)
  await page.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise
  return canvas
}

// Portaled to <body> — the journey card (and this section's own scroll-reveal transform,
// see .pr-reveal in present.css) would otherwise trap this position:fixed lightbox inside
// their transformed/blurred ancestor instead of covering the viewport. Same pattern as
// present/Lightbox.jsx, journey/modules/PresentationLink.jsx, and RenderingBookViewer.jsx.
function Lightbox({ items, index, onClose, onNav }) {
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') { e.stopPropagation(); onClose() }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); e.stopPropagation(); onNav(-1) }
      else if (e.key === 'ArrowRight') { e.preventDefault(); e.stopPropagation(); onNav(1) }
    }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [onClose, onNav])
  if (index == null) return null
  const it = items[index]
  return createPortal(
    <div className="pr-sk-lb is-active" onClick={onClose}>
      <button className={'pr-sk-lb-arrow' + (index <= 0 ? ' is-hidden' : '')} onClick={e => { e.stopPropagation(); onNav(-1) }}>‹</button>
      <div className="pr-sk-lb-inner" onClick={e => e.stopPropagation()}>
        <img className="pr-sk-lb-img" src={it.src} alt={it.title} />
        {it.title ? (
          <div className="pr-sk-lb-cap">
            <div className="pr-sk-lb-title">{it.title}</div>
          </div>
        ) : null}
      </div>
      <button className={'pr-sk-lb-arrow' + (index >= items.length - 1 ? ' is-hidden' : '')} onClick={e => { e.stopPropagation(); onNav(1) }}>›</button>
      <button className="pr-sk-lb-close" onClick={onClose}>✕</button>
    </div>,
    document.body
  )
}

export default function SketchWall({ section }) {
  const editable = useEditable()
  const { recordId, reload, mode, token } = usePresentation()
  const projectId = section?.data?.projectId || null
  const all = section?.data?.sketches || []
  const sketches = all.filter(s => s && (s.src || editable))
  const visible = all.filter(s => s && s.src)
  const [lb, setLb] = useState(null)
  const wallRef = useRef(null)
  const fileRef = useRef(null)
  const { resolveUpload, picker } = usePdfPagePicker()

  // Drag-drop reorder (edit mode only) — FLIP-animated, same pattern as SiteAnalysisModal's
  // Objects panel / ProgramModelModal's Views list, adapted to a horizontal track: a ghost board
  // follows the pointer 1:1 while the other boards slide (translateX FLIP) into their new slots as
  // the live splice changes their position, so it's obvious where the drop will land. The order
  // commits to Airtable once, on release — not per-move.
  const idsKey2 = sketches.map(s => s.id).join(',')
  const [order, setOrder] = useState(() => sketches.map(s => s.id))
  const draggingRef = useRef(false)
  const boardRefs = useRef({})
  const flipRef = useRef(null)
  const [dragItem, setDragItem] = useState(null)   // { id, rect, dx } | null
  useEffect(() => {
    if (draggingRef.current) return
    setOrder(sketches.map(s => s.id))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey2])
  useLayoutEffect(() => {
    const before = flipRef.current
    if (!before) return
    flipRef.current = null
    for (const id of Object.keys(before)) {
      const el = boardRefs.current[id]
      if (!el) continue
      const delta = before[id] - el.getBoundingClientRect().left
      if (!delta) continue
      el.style.transition = 'none'
      el.style.transform = 'translateX(' + delta + 'px)'
      requestAnimationFrame(() => { el.style.transition = 'transform 180ms ease'; el.style.transform = '' })
    }
  })
  function dragStart(e, id) {
    e.preventDefault(); e.stopPropagation()
    const el = boardRefs.current[id]
    if (!el) return
    const rect = el.getBoundingClientRect()
    const startX = e.clientX
    draggingRef.current = true
    let cur = order
    const startIdx = cur.indexOf(id)
    // Column pitch (board width + gap) measured live off a neighbor so it tracks the responsive
    // clamp()'d board width — never hardcoded.
    let colW = rect.width + 32
    const neighborId = cur[startIdx === 0 ? 1 : startIdx - 1]
    const neighborEl = neighborId && boardRefs.current[neighborId]
    if (neighborEl) colW = Math.abs(neighborEl.getBoundingClientRect().left - rect.left) || colW
    setDragItem({ id, rect, dx: 0 })
    function move(ev) {
      const dx = ev.clientX - startX
      setDragItem(d => (d && d.id === id ? { ...d, dx } : d))
      const newIdx = Math.max(0, Math.min(cur.length - 1, startIdx + Math.round(dx / colW)))
      const idxNow = cur.indexOf(id)
      if (newIdx !== idxNow) {
        const before = {}
        for (const id2 of cur) { if (id2 === id) continue; const el2 = boardRefs.current[id2]; if (el2) before[id2] = el2.getBoundingClientRect().left }
        flipRef.current = before
        const next = [...cur]
        next.splice(idxNow, 1); next.splice(newIdx, 0, id)
        cur = next
        setOrder(next)
      }
    }
    function up() {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      setDragItem(null)
      draggingRef.current = false
      reorderSketches(cur).then(reload).catch(() => {})
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
  }

  // Which sketches are PDF-backed "Concept Drawing" boards — a row existing in
  // concept_drawings is the ONLY signal (see services/conceptDrawings.js). Reloaded whenever the
  // sketch list changes (new upload, delete) so a freshly-added PDF board is clickable right away.
  // Team mode gets the richer { [sketchId]: {id, ...} } shape (drawing.id needed for cleanup on
  // delete); client mode gets a plain boolean map. Both are truthy/falsy the same way for display.
  const [drawingsMap, setDrawingsMap] = useState({})
  const idsKey = all.map(s => s?.id).filter(Boolean).join(',')
  useEffect(() => {
    let cancelled = false
    const ids = idsKey ? idsKey.split(',') : []
    if (!ids.length) { setDrawingsMap({}); return }
    const req = mode === 'client' ? getConceptDrawingsExistForClient(token, ids) : getConceptDrawingsForSketches(ids)
    req.then(m => { if (!cancelled) setDrawingsMap(m || {}) }).catch(() => {})
    return () => { cancelled = true }
  }, [idsKey, mode, token])

  // Staggered slide-in as boards enter the wall viewport (matches the original).
  useEffect(() => {
    const wall = wallRef.current
    if (!wall) return
    const boards = wall.querySelectorAll('.pr-sk-board')
    const obs = new IntersectionObserver(entries => {
      entries.forEach(en => {
        if (!en.isIntersecting) return
        const sibs = Array.from(en.target.parentNode.children)
        const idx = sibs.indexOf(en.target)
        setTimeout(() => en.target.classList.add('is-in'), Math.max(0, idx) * 80)
        obs.unobserve(en.target)
      })
    }, { root: wall, threshold: 0.15, rootMargin: '0px 60px' })
    boards.forEach(b => obs.observe(b))
    return () => obs.disconnect()
  }, [sketches.length, editable])

  const includeValue = section?.data?.includeValue || 'Interior Concept Package'
  async function addFile(file) {
    if (IS_PDF(file)) {
      const resolved = await resolveUpload(file)   // page-picker if multi-page; null if cancelled
      if (!resolved) return
      const thumbCanvas = await renderPdfThumbnail(resolved)
      const thumbBlob = await new Promise(res => thumbCanvas.toBlob(res, 'image/png'))
      const niceTitle = (file.name || 'Concept Drawing').replace(/\.pdf$/i, '')
      const { url } = await uploadImage(thumbBlob, niceTitle + '.png')
      const sketchId = await createSketch(recordId, url, all.length + 1, includeValue)
      await patchSketch(sketchId, { 'Sketch Title': niceTitle })
      await uploadConceptDrawing(sketchId, projectId, resolved, thumbBlob)
      reload()
      // Open straight into the tool so the presenter sets the scale before anyone else sees it.
      window.dispatchEvent(new CustomEvent('open-concept-drawing-modal', {
        detail: { sketchId, projectId, title: niceTitle, mode: 'presenter' },
      }))
      return
    }
    const { url } = await uploadImage(file, file.name)
    await createSketch(recordId, url, all.length + 1, includeValue)
    reload()
  }
  async function del(id) {
    if (!(await confirmDialog({ title: 'Delete sketch?', message: 'Delete this sketch?', confirmLabel: 'Delete', danger: true }))) return
    const drawing = drawingsMap[id]
    if (drawing && drawing.id) { try { await deleteConceptDrawing(drawing.id) } catch { /* best-effort orphan cleanup */ } }
    deleteSketch(id).then(reload).catch(() => {})
  }
  function openBoard(s) {
    if (drawingsMap[s.id]) {
      window.dispatchEvent(new CustomEvent('open-concept-drawing-modal', {
        detail: {
          sketchId: s.id, projectId, title: s.title || 'Concept Drawing',
          mode: mode === 'client' ? 'client' : 'presenter',
          journeyToken: mode === 'client' ? token : undefined,
        },
      }))
      return
    }
    if (s.src) setLb(visible.findIndex(v => v.id === s.id))
  }

  if (!sketches.length && !editable) return null
  const byId = new Map(sketches.map(s => [s.id, s]))
  const dragged = dragItem && byId.get(dragItem.id)
  return (
    <section className="pr-sk-section pr-reveal" data-section-id={section.id}>
      <div className="pr-sk-inner">
        <div className="pr-sk-label">
          Sketches
          {editable ? (
            <>
              <button className="pr-sk-add" onClick={() => fileRef.current && fileRef.current.click()}>+ Add</button>
              <input ref={fileRef} type="file" accept="image/*,application/pdf" style={{ display: 'none' }}
                onChange={e => { const f = e.target.files && e.target.files[0]; if (f) addFile(f); e.target.value = '' }} />
              {picker}
            </>
          ) : null}
        </div>
        <div className="pr-sk-wall" ref={wallRef}>
          <div className={'pr-sk-track' + (dragItem ? ' is-reordering' : '')}>
            {order.map(id => {
              const s = byId.get(id)
              if (!s) return null
              return (
                <div key={s.id} className="pr-sk-board" ref={el => { boardRefs.current[s.id] = el }}
                  style={{ opacity: dragItem && dragItem.id === s.id ? 0.35 : undefined }}>
                  {editable ? (
                    <span className="pr-sk-drag-handle" title="Drag to reorder"
                      onPointerDown={e => dragStart(e, s.id)}>⠿</span>
                  ) : null}
                  <div className="pr-sk-paper" onClick={() => openBoard(s)}
                    style={{ cursor: s.src ? (drawingsMap[s.id] ? 'pointer' : 'zoom-in') : 'default' }}>
                    {s.src ? <img src={s.src} alt={s.title} loading="lazy" /> : <div className="pr-sk-empty">No image</div>}
                    {drawingsMap[s.id] ? <span className="pr-sk-drawing-badge">Drawing</span> : null}
                  </div>
                  {editable ? (
                    <>
                      <EditableText value={s.title} as="div" className="pr-sk-title" plain placeholder="Title"
                        onCommit={v => patchSketch(s.id, { 'Sketch Title': v }).catch(() => {})} />
                      <select className="pr-sk-type" defaultValue={s.sketchType || ''}
                        onChange={e => patchSketch(s.id, { 'Sketch Type': e.target.value }).catch(() => {})}>
                        {SKETCH_TYPES.map(t => <option key={t} value={t}>{t || '— Type —'}</option>)}
                      </select>
                      <button className="pr-sk-del" onClick={() => del(s.id)}>Delete</button>
                    </>
                  ) : (
                    s.title ? <div className="pr-sk-title">{s.title}</div> : null
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </div>
      {dragged && (
        <div className="pr-sk-drag-ghost" style={{
          position: 'fixed', left: dragItem.rect.left + dragItem.dx, top: dragItem.rect.top,
          width: dragItem.rect.width,
        }}>
          <div className="pr-sk-paper">
            {dragged.src ? <img src={dragged.src} alt="" /> : <div className="pr-sk-empty">No image</div>}
          </div>
          {dragged.title ? <div className="pr-sk-title">{dragged.title}</div> : null}
        </div>
      )}
      <Lightbox items={visible} index={lb} onClose={() => setLb(null)}
        onNav={d => setLb(i => Math.max(0, Math.min(visible.length - 1, i + d)))} />
    </section>
  )
}
